import type { Webform } from '@formio/js';

/**
 * Wires dismissal + body-scroll-lock for an authored modal popup built inside a
 * Form.io HTML component (markup uses `srp-modal*` classes; styles in formio.css).
 *
 * Form.io injects a conditionally-hidden HTML component's markup only once its
 * condition is first met, so we gate on the form schema (not the live DOM) and
 * use delegated listeners + an observer that react after the markup is injected.
 * Listeners/observer are bound to `wrapper` (released with it on unmount); the
 * destroy hook clears any lingering scroll lock.
 */
export function setupAuthoredModal(instance: Webform, wrapper: Element): void {
  let schemaHasModal = false;
  try {
    schemaHasModal = JSON.stringify(instance.form ?? {}).includes('srp-modal');
  } catch {
    schemaHasModal = false;
  }
  if (!schemaHasModal) return;

  const dismiss = (modal: Element) => modal.classList.add('srp-modal--dismissed');

  // Lock body scroll while any modal is visible. getClientRects() is empty when
  // the modal is display:none (dismissed or hidden) or not yet injected.
  const syncScrollLock = () => {
    const anyOpen = Array.from(wrapper.querySelectorAll<HTMLElement>('.srp-modal')).some(
      (modal) => modal.getClientRects().length > 0
    );
    document.body.classList.toggle('srp-modal-open', anyOpen);
  };

  // Close button always closes; backdrop/Escape close only modals that opt in
  // via `data-modal-outside-click`. Dismissing keeps the user's radio answer.
  wrapper.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const closer = target.closest('.srp-modal__close, [data-srp-modal-close]');
    if (closer) {
      closer.closest('.srp-modal')?.classList.add('srp-modal--dismissed');
      syncScrollLock();
      return;
    }
    if (target.classList.contains('srp-modal') && target.hasAttribute('data-modal-outside-click')) {
      dismiss(target);
      syncScrollLock();
    }
  });

  wrapper.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key !== 'Escape') return;
    wrapper
      .querySelectorAll('.srp-modal[data-modal-outside-click]:not(.srp-modal--dismissed)')
      .forEach(dismiss);
    syncScrollLock();
  });

  // Form.io injects/removes the markup (childList) and toggles `formio-hidden`.
  // On the hidden->shown transition, clear dismissed modals so re-selecting the
  // option reopens them; re-sync the scroll lock on any visibility change.
  const observer = new MutationObserver((mutations) => {
    let touched = false;
    for (const mutation of mutations) {
      const el = mutation.target as HTMLElement;
      if (mutation.type === 'childList') {
        touched = true;
        continue;
      }
      const isComponent = el.classList?.contains('formio-component');
      if (mutation.attributeName === 'class' && isComponent) {
        touched = true;
        const wasHidden = mutation.oldValue?.includes('formio-hidden');
        if (wasHidden && !el.classList.contains('formio-hidden')) {
          el.querySelectorAll('.srp-modal--dismissed').forEach((modal) =>
            modal.classList.remove('srp-modal--dismissed')
          );
        }
      } else if (mutation.attributeName === 'style' && isComponent) {
        touched = true;
      } else if (el.classList?.contains('srp-modal')) {
        touched = true;
      }
    }
    if (touched) syncScrollLock();
  });
  observer.observe(wrapper, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ['style', 'class'],
  });

  // Catch a modal already visible on first render (e.g. a radio default).
  syncScrollLock();

  instance.on('destroy', () => {
    observer.disconnect();
    document.body.classList.remove('srp-modal-open');
  });
}
